import React, { createContext, useContext, useState, useCallback } from 'react';

// Create Error Context
const ErrorContext = createContext(null);

// Custom hook to use error context
export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

// Error types
export const ERROR_TYPES = {
  AUTHENTICATION: 'authentication',
  NETWORK: 'network',
  VALIDATION: 'validation',
  PERMISSION: 'permission',
  GENERAL: 'general'
};

// Error Provider Component
export const ErrorProvider = ({ children }) => {
  const [errors, setErrors] = useState([]);
  const [globalError, setGlobalError] = useState(null);

  // Remove an error - declared first since addError depends on it
  const removeError = useCallback((errorId) => {
    setErrors(prev => prev.filter(error => error.id !== errorId));
  }, []);

  // Add an error
  const addError = useCallback((error) => {
    const errorId = crypto.randomUUID();
    const errorObject = {
      id: errorId,
      message: error.message || error,
      type: error.type || ERROR_TYPES.GENERAL,
      timestamp: new Date().toISOString(),
      details: error.details || null,
      action: error.action || null
    };

    setErrors(prev => [...prev, errorObject]);

    // Auto-remove error after 5 seconds unless it's critical
    if (error.type !== ERROR_TYPES.AUTHENTICATION && error.type !== ERROR_TYPES.PERMISSION) {
      setTimeout(() => {
        removeError(errorId);
      }, 5000);
    }

    return errorId;
  }, [removeError]);

  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors([]);
    setGlobalError(null);
  }, []);

  // Set global error (for critical errors that should block the UI)
  const setGlobalErrorHandler = useCallback((error) => {
    setGlobalError({
      message: error.message || error,
      type: error.type || ERROR_TYPES.GENERAL,
      timestamp: new Date().toISOString(),
      details: error.details || null
    });
  }, []);

  // Clear global error
  const clearGlobalError = useCallback(() => {
    setGlobalError(null);
  }, []);

  // Helper functions for specific error types
  const addAuthError = useCallback((message, details = null) => {
    return addError({
      message,
      type: ERROR_TYPES.AUTHENTICATION,
      details
    });
  }, [addError]);

  const addNetworkError = useCallback((message, details = null) => {
    return addError({
      message,
      type: ERROR_TYPES.NETWORK,
      details,
      action: 'Please check your internet connection and try again.'
    });
  }, [addError]);

  const addValidationError = useCallback((message, details = null) => {
    return addError({
      message,
      type: ERROR_TYPES.VALIDATION,
      details
    });
  }, [addError]);

  const addPermissionError = useCallback((message, details = null) => {
    return addError({
      message,
      type: ERROR_TYPES.PERMISSION,
      details
    });
  }, [addError]);

  // Context value
  const contextValue = {
    // State
    errors,
    globalError,
    hasErrors: errors.length > 0,
    hasGlobalError: globalError !== null,

    // Actions
    addError,
    removeError,
    clearErrors,
    setGlobalError: setGlobalErrorHandler,
    clearGlobalError,

    // Helpers
    addAuthError,
    addNetworkError,
    addValidationError,
    addPermissionError
  };

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

export default ErrorContext;
